import React, { useState, useEffect, useRef } from "react";
import { 
  FiSearch, FiFilter, FiMoreVertical, FiTrendingUp, FiTrendingDown,
  FiClock, FiAlertTriangle, FiUserCheck, FiBookOpen,
  FiMessageSquare, FiCalendar, FiChevronDown, FiPlus, FiUsers, FiHome, FiUser
} from "react-icons/fi";
import ChangePasswordModal from "../account/ChangePasswordModal";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import api from "../connector/api";

function Dashboard() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [user, setUser] = useState({ first_name: "Achmad", last_name: "Hakim", email: "achmadhakim@gmail.com" });
  const [notifOpen, setNotifOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [ticketSearch, setTicketSearch] = useState("");
  const [activeTab, setActiveTab] = useState("Today");

  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [loadingPeriod, setLoadingPeriod] = useState(true);

  // Dynamic Dashboard Stats State
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  const notificationDropdownRef = useRef(null);

  // Format Today's Date
  const todayDate = new Date().toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric"
  });

  useEffect(() => {
    const storedPerms = sessionStorage.getItem("permissions");
    const storedUser = sessionStorage.getItem("user");
    
    if (storedPerms) {
      try {
        setPermissions(JSON.parse(storedPerms));
      } catch (err) {
        console.error("Failed to parse permissions:", err);
      }
    }

    if (storedUser) {
      try {
        const userObj = JSON.parse(storedUser);
        setUser({
          first_name: userObj.first_name || "Achmad",
          last_name: userObj.last_name || "Hakim",
          username: userObj.username || "achmadhakim",
          email: userObj.email || "achmadhakim@gmail.com"
        });
      } catch (err) {
        console.error("Failed to parse user session:", err);
      }
    }

    const fetchCurrentPeriod = async () => {
      try {
        const res = await api.get("year-terms/get-set-year-term/");
        setCurrentPeriod(res.data);
      } catch (err) {
        console.error("Failed to fetch active school year-term:", err);
      } flexFinally: {
        setLoadingPeriod(false);
      }
    };

    const fetchDashboardStats = async () => {
      try {
        const res = await api.get("dashboard/stats/");
        setDashboardStats(res.data);
      } catch (err) {
        console.error("Failed to fetch dashboard stats:", err);
      } finally {
        setLoadingStats(false);
      }
    };

    fetchCurrentPeriod();
    fetchDashboardStats();
  }, []);

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = "/login";
  };

  const yearDisplay = currentPeriod?.school_year_detail?.name || currentPeriod?.school_year || "2025/2026";
  const termDisplay = currentPeriod?.term_detail?.name || currentPeriod?.term || "Term 1";

  // Dynamic Metrics Data with Safe Fallback Values
  const metrics = dashboardStats?.metrics || { total_students: 0, total_employees: 0, total_rooms: 0 };
  const graphData = dashboardStats?.registration_graph?.data || [];
  const classroomsList = dashboardStats?.classrooms_preview || [];
  const registrationsList = dashboardStats?.student_registrations_preview || [];

  // Filter Registrations Safely
  const filteredRegistrations = registrationsList.filter(reg => {
    const nameMatch = reg?.name ? reg.name.toLowerCase().includes(ticketSearch.toLowerCase()) : false;
    const regIdMatch = reg?.regId ? reg.regId.toLowerCase().includes(ticketSearch.toLowerCase()) : false;
    return nameMatch || regIdMatch;
  });

  // Dynamic / Fallback Activity Feed Items
  const activities = [
    { time: "11:20 AM", title: "Registration Updated", desc: "Student registration processed", icon: <FiClock className="w-3 h-3 text-blue-600" /> },
    { time: "11:15 AM", title: "New Student Added", desc: "Student profile registered", icon: <FiUserCheck className="w-3 h-3 text-emerald-600" /> },
    { time: "11:00 AM", title: "Teacher Assigned", desc: "Classroom assigned to new teacher", icon: <FiUserCheck className="w-3 h-3 text-indigo-600" /> },
    { time: "10:45 AM", title: "Capacity Alert", desc: "Classroom reached capacity limit", icon: <FiAlertTriangle className="w-3 h-3 text-rose-600" /> },
    { time: "10:30 AM", title: "Curriculum Update", desc: "New term syllabus uploaded", icon: <FiBookOpen className="w-3 h-3 text-cyan-600" /> },
    { time: "10:15 AM", title: "Parent Inquiry", desc: "Inquiry received regarding enrollment", icon: <FiMessageSquare className="w-3 h-3 text-amber-600" /> },
  ];

  return (
    <div className="h-screen w-screen bg-[#f8f9fa] flex font-dynamic-title text-zinc-700 antialiased overflow-hidden selection:bg-zinc-900 selection:text-white">
      
      {/* Sidebar Component */}
      <Sidebar
        permissions={permissions}
        user={user}
        handleLogout={handleLogout}
        onOpenChangePassword={() => setIsModalOpen(true)}
      />

      {/* Main Workspace */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        
        {/* Header */}
        <Header
          loadingPeriod={loadingPeriod}
          yearDisplay={yearDisplay}
          termDisplay={termDisplay}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          notifOpen={notifOpen}
          setNotifOpen={setNotifOpen}
          notificationDropdownRef={notificationDropdownRef}
        />

        {/* Dashboard Content Workspace */}
        <main className="p-3 md:p-4 flex-1 flex flex-col gap-3 min-h-0 overflow-hidden">
          
          {/* Greeting Banner */}
          <div className="flex items-center justify-between shrink-0">
            <div>
              <h1 className="text-base md:text-lg font-bold text-zinc-900 tracking-tight flex items-center gap-2">
                Hello, {user.last_name} {user.first_name} 👋
              </h1>
              <p className="text-[11px] text-zinc-500">
                Here are the latest insights and metrics from your school system.
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold text-zinc-600 bg-white border border-zinc-200 px-2.5 py-1 rounded-lg shadow-2xs">
                📅 {todayDate}
              </span>
              <button className="flex items-center gap-1 text-[11px] font-medium bg-white border border-zinc-200 px-2.5 py-1 rounded-lg shadow-2xs hover:bg-zinc-50 transition-colors">
                <FiCalendar className="w-3 h-3 text-zinc-500" />
                <span>This Academic Year</span>
                <FiChevronDown className="w-3 h-3 text-zinc-400" />
              </button>
            </div>
          </div>

          {/* Top Metric Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 shrink-0">
            
            {/* Metric 1: Total Students */}
            <div className="p-3 bg-white border border-zinc-200/90 rounded-xl shadow-2xs flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-zinc-500 flex items-center gap-1">
                  <FiUsers className="w-3 h-3 text-blue-500" /> Total Students
                </span>
                <FiMoreVertical className="w-3.5 h-3.5 text-zinc-400 cursor-pointer" />
              </div>
              <div className="mt-1 flex items-end justify-between">
                <div>
                  <span className="text-xl font-bold text-zinc-900">
                    {loadingStats ? "..." : (metrics.total_students ?? 0).toLocaleString()}
                  </span>
                  <div className="flex items-center gap-1 text-[10px] font-semibold text-emerald-600">
                    <FiTrendingUp className="w-3 h-3" />
                    <span>Active Enrollment</span>
                  </div>
                </div>
                <svg className="w-16 h-6 text-emerald-500" viewBox="0 0 100 30" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M0 20 Q 20 5, 40 18 T 80 8 T 100 15" strokeLinecap="round" />
                </svg>
              </div>
            </div>

            {/* Metric 2: Total Employees */}
            <div className="p-3 bg-white border border-zinc-200/90 rounded-xl shadow-2xs flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-zinc-500 flex items-center gap-1">
                  <FiUser className="w-3 h-3 text-emerald-500" /> Total Staff & Teachers
                </span>
                <FiMoreVertical className="w-3.5 h-3.5 text-zinc-400 cursor-pointer" />
              </div>
              <div className="mt-1 flex items-end justify-between">
                <div>
                  <span className="text-xl font-bold text-zinc-900">
                    {loadingStats ? "..." : (metrics.total_employees ?? 0).toLocaleString()}
                  </span>
                  <div className="flex items-center gap-1 text-[10px] font-semibold text-emerald-600">
                    <FiTrendingUp className="w-3 h-3" />
                    <span>Active Employees</span>
                  </div>
                </div>
                <svg className="w-16 h-6 text-teal-500" viewBox="0 0 100 30" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M0 18 Q 25 25, 50 10 T 80 15 T 100 5" strokeLinecap="round" />
                </svg>
              </div>
            </div>

            {/* Metric 3: Total Rooms */}
            <div className="p-3 bg-white border border-zinc-200/90 rounded-xl shadow-2xs flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-zinc-500 flex items-center gap-1">
                  <FiHome className="w-3 h-3 text-indigo-500" /> Total Rooms
                </span>
                <FiMoreVertical className="w-3.5 h-3.5 text-zinc-400 cursor-pointer" />
              </div>
              <div className="mt-1 flex items-end justify-between">
                <div>
                  <span className="text-xl font-bold text-zinc-900">
                    {loadingStats ? "..." : (metrics.total_rooms ?? 0).toLocaleString()}
                  </span>
                  <div className="flex items-center gap-1 text-[10px] font-semibold text-indigo-600">
                    <span>Facilities Available</span>
                  </div>
                </div>
                <svg className="w-16 h-6 text-indigo-500" viewBox="0 0 100 30" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M0 10 Q 30 25, 60 12 T 90 22 T 100 18" strokeLinecap="round" />
                </svg>
              </div>
            </div>

          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 flex-1 min-h-0 overflow-hidden">
            
            {/* Left Column (Chart + Registrations Preview Table) */}
            <div className="lg:col-span-2 flex flex-col gap-3 min-h-0 overflow-hidden">
              
              {/* Registration Trend Chart */}
              <div className="p-3 bg-white border border-zinc-200/90 rounded-xl shadow-2xs flex-1 flex flex-col justify-between min-h-0">
                <div className="flex items-center justify-between shrink-0">
                  <div className="flex items-baseline gap-2">
                    <h2 className="text-xs font-semibold text-zinc-900">Student Registration Trend</h2>
                    <span className="text-[10px] font-semibold text-zinc-500">
                      Comparison ({dashboardStats?.registration_graph?.previous_year || "Prev"} vs {dashboardStats?.registration_graph?.current_year || "Curr"})
                    </span>
                  </div>
                </div>

                {/* Bars Container */}
                <div className="h-28 flex items-end justify-between px-2 pt-2 relative z-10 shrink-0">
                  {graphData.length > 0 ? (
                    graphData.map((item, idx) => {
                      const maxVal = Math.max(...graphData.map(d => Math.max(d.current_year || 0, d.previous_year || 0)), 1);
                      const currentHeight = `${Math.round(((item.current_year || 0) / maxVal) * 100)}%`;

                      return (
                        <div key={idx} className="flex flex-col items-center group relative h-full justify-end flex-1">
                          {/* Tooltip on Hover */}
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute -top-6 z-20 bg-zinc-900 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded shadow whitespace-nowrap pointer-events-none">
                            {item.month}: {item.current_year} regs
                          </div>

                          <div 
                            style={{ height: currentHeight === "0%" ? "4px" : currentHeight }} 
                            className="w-3 md:w-4 rounded-xs transition-all duration-200 bg-zinc-900 hover:bg-zinc-700"
                          />
                          <span className="text-[9px] font-medium text-zinc-500 mt-1">{item.month}</span>
                        </div>
                      );
                    })
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-zinc-400">
                      {loadingStats ? "Loading graph..." : "No registration data available"}
                    </div>
                  )}
                </div>
              </div>

              {/* Recent Student Registrations Table */}
              <div className="p-3 bg-white border border-zinc-200/90 rounded-xl shadow-2xs flex-1 flex flex-col min-h-0">
                <div className="flex items-center justify-between gap-2 mb-2 shrink-0">
                  <h2 className="text-xs font-semibold text-zinc-900">Recent Registrations</h2>
                  
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <FiSearch className="w-3 h-3 text-zinc-400 absolute left-2 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Search student..."
                        value={ticketSearch}
                        onChange={(e) => setTicketSearch(e.target.value)}
                        className="pl-7 pr-2 py-0.5 text-[11px] bg-zinc-50 border border-zinc-200 rounded-md w-32 focus:outline-none focus:border-zinc-400"
                      />
                    </div>
                    <button className="flex items-center gap-1 text-[10px] bg-zinc-50 border border-zinc-200 px-2 py-0.5 rounded-md text-zinc-600 hover:bg-zinc-100">
                      <FiFilter className="w-3 h-3 text-zinc-500" />
                      <span>Filter</span>
                    </button>
                    <FiMoreVertical className="w-3.5 h-3.5 text-zinc-400 cursor-pointer" />
                  </div>
                </div>

                <div className="overflow-y-auto flex-1 min-h-0">
                  <table className="w-full text-left text-[11px]">
                    <thead className="sticky top-0 bg-white">
                      <tr className="border-b border-zinc-200 text-zinc-400 font-medium">
                        <th className="py-1.5 px-1">Reg ID</th>
                        <th className="py-1.5 px-1">Student Name</th>
                        <th className="py-1.5 px-1">Grade</th>
                        <th className="py-1.5 px-1">Guardian</th>
                        <th className="py-1.5 px-1">Date</th>
                        <th className="py-1.5 px-1">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100 text-zinc-700">
                      {filteredRegistrations.length > 0 ? (
                        filteredRegistrations.map((row, idx) => (
                          <tr key={row.regId || idx} className="hover:bg-zinc-50/80 transition-colors">
                            <td className="py-1.5 px-1 font-semibold text-zinc-900">{row.regId || "N/A"}</td>
                            <td className="py-1.5 px-1 font-medium truncate max-w-[120px]">{row.name || "N/A"}</td>
                            <td className="py-1.5 px-1">{row.grade || "-"}</td>
                            <td className="py-1.5 px-1 truncate max-w-[100px]">{row.guardian || "N/A"}</td>
                            <td className="py-1.5 px-1 text-zinc-500">{row.date || "-"}</td>
                            <td className="py-1.5 px-1">
                              <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded ${
                                row.status === 'Approved' ? 'text-emerald-600 bg-emerald-50' : 'text-amber-600 bg-amber-50'
                              }`}>
                                {row.status || "Pending"}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="py-4 text-center text-zinc-400 text-xs">
                            {loadingStats ? "Loading registrations..." : "No registration records found."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>

            {/* Right Column (Classrooms Preview & Activity Feed) */}
            <div className="flex flex-col min-h-0 gap-3 overflow-hidden">
              
              {/* Classroom Overview Preview */}
              <div className="p-3 bg-white border border-zinc-200/90 rounded-xl shadow-2xs flex-1 flex flex-col min-h-0">
                <div className="flex items-center justify-between mb-2 shrink-0">
                  <h2 className="text-xs font-semibold text-zinc-900">Classrooms Preview</h2>
                  <FiMoreVertical className="w-3.5 h-3.5 text-zinc-400 cursor-pointer" />
                </div>

                <div className="space-y-2 overflow-y-auto flex-1 min-h-0 pr-1">
                  {classroomsList.length > 0 ? (
                    classroomsList.map((cls, idx) => (
                      <div key={cls.id || idx} className="p-2 border border-zinc-100 rounded-lg bg-zinc-50/50 flex items-center justify-between">
                        <div>
                          <h3 className="text-[11px] font-semibold text-zinc-900">{cls.name}</h3>
                          <p className="text-[10px] text-zinc-500">Teacher: {cls.teacher}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-[11px] font-bold text-zinc-800">{cls.students}/{cls.capacity}</span>
                          <span className={`block text-[9px] font-semibold ${cls.status === 'Full' ? 'text-rose-600' : 'text-emerald-600'}`}>
                            {cls.status}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-zinc-400 text-center py-4">
                      {loadingStats ? "Loading classrooms..." : "No classrooms found."}
                    </div>
                  )}
                </div>
              </div>

              {/* Activity Feed */}
              <div className="p-3 bg-white border border-zinc-200/90 rounded-xl shadow-2xs flex-1 flex flex-col min-h-0">
                <div className="flex items-center justify-between mb-2 shrink-0">
                  <h2 className="text-xs font-semibold text-zinc-900">Latest Activity Updates</h2>
                </div>

                <div className="grid grid-cols-3 gap-1 p-0.5 bg-zinc-100 rounded-md text-center text-[10px] font-semibold mb-2 shrink-0">
                  {["Today", "Yesterday", "This week"].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`py-0.5 rounded transition-all ${
                        activeTab === tab ? "bg-zinc-900 text-white shadow-2xs" : "text-zinc-500 hover:text-zinc-900"
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                <div className="space-y-2 overflow-y-auto flex-1 min-h-0 pr-1">
                  {activities.map((act, i) => (
                    <div key={i} className="flex items-start gap-2 p-1.5 rounded-lg hover:bg-zinc-50 transition-colors">
                      <div className="w-6 h-6 rounded bg-zinc-100 flex items-center justify-center shrink-0 mt-0.5">
                        {act.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className="text-[11px] font-semibold text-zinc-900 truncate">{act.title}</h3>
                          <span className="text-[9px] text-zinc-400">{act.time}</span>
                        </div>
                        <p className="text-[10px] text-zinc-500 truncate">{act.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

              </div>
            </div>

          </div>

        </main>
      </div>
      {isModalOpen && <ChangePasswordModal onClose={() => setIsModalOpen(false)} />}
    </div>
  );
}

export default Dashboard;